import {cva} from 'class-variance-authority'

/**
 * MUI `Chip` (v3). Pill container; `color` encodes the Figma "Selected" axis
 * (`primary` = selected, `secondary` = unselected). `variant` filled/outlined,
 * `size` medium (32px) / small (24px). Interaction states (hover/focus/pressed)
 * apply when the chip is `interactive` — clickable *or* deletable, matching MUI,
 * where `.MuiChip-deletable` picks up the same hover fill as `.MuiChip-clickable`.
 */
export const chipVariants = cva(
  'relative inline-flex items-center overflow-hidden rounded-full transition-colors focus-visible:outline-none',
  {
    variants: {
      variant: {
        filled: '',
        outlined: 'border border-solid',
      },
      color: {
        primary: '',
        secondary: '',
      },
      size: {
        medium: 'min-h-8 p-1',
        small: 'min-h-6 max-h-6 px-1 py-[3px]',
      },
      clickable: {
        true: 'cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        false: '',
      },
      interactive: {
        true: '',
        false: '',
      },
      disabled: {
        true: 'pointer-events-none cursor-not-allowed opacity-[0.38]',
        false: '',
      },
    },
    compoundVariants: [
      // --- resting fills / borders / text ---
      {variant: 'filled', color: 'secondary', class: 'bg-background-subdued text-text-primary'},
      {variant: 'filled', color: 'primary', class: 'bg-primary text-text-inverse'},
      {variant: 'outlined', color: 'secondary', class: 'border-divider text-secondary-dark'},
      {variant: 'outlined', color: 'primary', class: 'border-primary text-primary'},
      // --- clickable hover / focus / pressed (skip when disabled) ---
      {
        variant: 'filled',
        color: 'secondary',
        interactive: true,
        disabled: false,
        class:
          'hover:bg-secondary-light focus-visible:bg-secondary-light active:bg-secondary-light active:shadow-elevation-1',
      },
      {
        variant: 'filled',
        color: 'primary',
        interactive: true,
        disabled: false,
        class:
          'hover:bg-primary-dark focus-visible:bg-primary-dark active:bg-primary-dark active:shadow-elevation-1',
      },
      {
        variant: 'outlined',
        color: 'secondary',
        interactive: true,
        disabled: false,
        class: 'hover:bg-secondary-states-hover active:bg-secondary-states-hover',
      },
      {
        variant: 'outlined',
        color: 'primary',
        interactive: true,
        disabled: false,
        class: 'hover:bg-action-hover active:bg-action-hover',
      },
    ],
    defaultVariants: {
      variant: 'filled',
      color: 'secondary',
      size: 'medium',
      clickable: false,
      interactive: false,
      disabled: false,
    },
  },
)
